import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface ChatPageContextIds {
  userId?: string;
  saleId?: string;
  partnerId?: string;
  productId?: string;
  claimId?: string;
  orderId?: string;
  categoryId?: string;
  storeId?: string;
  sellerId?: string;
  productEditingRequestId?: string;
  claimDocumentId?: string;
  articleId?: string;
  partnerSlug?: string;
  blogSlug?: string;
  page?: string;
}

export interface ChatPageContext {
  url: string;
  path: string;
  area: 'admin' | 'partner' | 'user' | 'site' | 'auth' | 'unknown';
  screen: string;
  ids: ChatPageContextIds;
}

@Injectable({ providedIn: 'root' })
export class ChatbotPageContextService {
  private router = inject(Router);

  /** Snapshot da rota ativa para enviar ao POST /assistant/chat. */
  capture(): ChatPageContext {
    const root = this.router.routerState.snapshot.root;
    const params = this.collectParams(root);
    const query = this.collectQuery(root);
    const segments = this.collectSegments(root);
    const path = this.normalizePath(this.router.url);
    const ids = this.extractIds(params, query, segments, path);
    const area = this.resolveArea(path);
    const screen = this.resolveScreen(path, segments, ids);

    return {
      url: path,
      path,
      area,
      screen,
      ids,
    };
  }

  /** Chave estável da tela (sem paginação) para cache das sugestões. */
  pageCacheKey(ctx: ChatPageContext = this.capture()): string {
    const screen = ctx.screen.replace(/-\d+$/, '');
    return `${ctx.area}:${screen}`.slice(0, 80);
  }

  private collectParams(route: ActivatedRouteSnapshot): Record<string, string> {
    const out: Record<string, string> = {};
    let node: ActivatedRouteSnapshot | null = route;

    while (node) {
      for (const [key, value] of Object.entries(node.params ?? {})) {
        if (value !== undefined && value !== null && String(value).trim()) {
          out[key] = String(value);
        }
      }
      node = node.firstChild;
    }

    return out;
  }

  private collectQuery(route: ActivatedRouteSnapshot): Record<string, string> {
    const out: Record<string, string> = {};
    let node: ActivatedRouteSnapshot | null = route;

    while (node) {
      for (const [key, value] of Object.entries(node.queryParams ?? {})) {
        if (value !== undefined && value !== null && String(value).trim()) {
          out[key] = String(value);
        }
      }
      node = node.firstChild;
    }

    return out;
  }

  private collectSegments(route: ActivatedRouteSnapshot): string[] {
    const segments: string[] = [];
    let node: ActivatedRouteSnapshot | null = route;

    while (node) {
      for (const segment of node.url ?? []) {
        if (segment.path) segments.push(segment.path);
      }
      node = node.firstChild;
    }

    return segments;
  }

  private normalizePath(url: string): string {
    const withoutQuery = url.split('?')[0]?.split('#')[0] ?? url;
    return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
  }

  private extractIds(
    params: Record<string, string>,
    query: Record<string, string>,
    segments: string[],
    path: string
  ): ChatPageContextIds {
    const ids: ChatPageContextIds = {};

    const assignUuid = (key: keyof ChatPageContextIds, value?: string) => {
      if (value && UUID_RE.test(value)) {
        ids[key] = value;
      }
    };

    assignUuid('userId', params['userId']);
    assignUuid('saleId', params['saleId']);
    assignUuid('partnerId', params['partnerId']);
    assignUuid('productId', params['productId']);
    assignUuid('claimId', params['claimId']);
    assignUuid('orderId', params['orderId']);
    assignUuid('categoryId', params['categoryId']);
    assignUuid('storeId', params['storeId']);
    assignUuid('sellerId', params['sellerId']);
    assignUuid(
      'productEditingRequestId',
      params['productEditingRequestId']
    );
    assignUuid('claimDocumentId', params['claimDocumentId']);
    assignUuid(
      'articleId',
      path.includes('/blog/editar/') ? params['id'] : query['articleId']
    );

    if (params['page'] && /^\d+$/.test(params['page'])) {
      ids.page = params['page'];
    }

    const slugFromSite = this.extractSitePartnerSlug(path, segments);
    if (slugFromSite) {
      ids.partnerSlug = slugFromSite;
    }

    const blogSlug = this.extractSiteBlogSlug(path, params, segments);
    if (blogSlug) {
      ids.blogSlug = blogSlug;
    }

    return ids;
  }

  private extractSitePartnerSlug(
    path: string,
    segments: string[]
  ): string | undefined {
    const meIdx = segments.indexOf('mobilidade-eletrica');
    if (meIdx >= 0 && segments[meIdx + 1]) {
      const slug = segments[meIdx + 1];
      if (slug !== 'contratacao' && !UUID_RE.test(slug)) {
        return slug;
      }
    }

    const psIdx = segments.indexOf('paineis-solares');
    if (psIdx >= 0 && segments[psIdx + 1]) {
      const slug = segments[psIdx + 1];
      if (slug !== 'contratacao' && !UUID_RE.test(slug)) {
        return slug;
      }
    }

    return undefined;
  }

  private extractSiteBlogSlug(
    path: string,
    params: Record<string, string>,
    segments: string[]
  ): string | undefined {
    const isPublicBlog =
      path.startsWith('/movimento-wosi/') || path.startsWith('/blog/');
    if (!isPublicBlog) return undefined;
    if (
      path.startsWith('/movimento-wosi/preview/') ||
      path.startsWith('/blog/preview/') ||
      path.startsWith('/movimento-wosi/categoria/') ||
      path.startsWith('/blog/categoria/')
    ) {
      return undefined;
    }

    const slug = params['slug'] || segments[1];
    if (!slug || slug === 'preview' || slug === 'categoria') return undefined;
    if (slug.length > 180) return undefined;
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return undefined;
    return slug;
  }

  private resolveArea(path: string): ChatPageContext['area'] {
    if (path.startsWith('/login') || path.startsWith('/criar-conta')) {
      return 'auth';
    }
    if (path.startsWith('/minha-conta')) {
      if (
        path.includes('/parceiro/') ||
        path.includes('/minha-pagina') ||
        path.includes('/lojas-e-vendedores')
      ) {
        return 'partner';
      }
      if (
        path.includes('/admin/') ||
        this.isAdminMinhaContaPath(path)
      ) {
        return 'admin';
      }
      return 'user';
    }
    if (
      path.startsWith('/mobilidade-eletrica') ||
      path.startsWith('/paineis-solares') ||
      path.startsWith('/seguro-') ||
      path.startsWith('/movimento-wosi') ||
      path.startsWith('/blog') ||
      path === '/' ||
      path.startsWith('/error')
    ) {
      return 'site';
    }
    return 'unknown';
  }

  private isAdminMinhaContaPath(path: string): boolean {
    const adminMarkers = [
      '/usuarios/',
      '/clientes/',
      '/parceiros/',
      '/produtos/',
      '/sinistro/',
      '/solicitacoes/',
      '/auditoria',
      '/cupons-de-desconto',
      '/coberturas',
      '/notificacoes',
      '/relatorios',
      '/comissoes',
      '/financeiro',
      '/design-system',
      '/blog',
      '/inteligencia',
      '/agentes',
      '/automacoes',
      '/conhecimento',
      '/guardrails',
      '/configuracoes/inteligencia',
    ];
    return adminMarkers.some((marker) => path.includes(marker));
  }

  private resolveScreen(
    path: string,
    segments: string[],
    ids: ChatPageContextIds
  ): string {
    if (ids.orderId && path.includes('/contratacao/')) {
      return 'contratacao-pedido';
    }
    if (ids.userId && path.includes('/usuarios/visualizar/')) {
      return 'admin-usuario';
    }
    if (ids.saleId && path.includes('/clientes/visualizar/')) {
      if (path.includes('/produto')) {
        return 'admin-cliente-produto';
      }
      if (path.includes('/apolice')) {
        return 'admin-cliente-apolice';
      }
      if (path.includes('/arquivos')) {
        return 'admin-cliente-arquivos';
      }
      if (path.includes('/aprovacao')) {
        return 'admin-cliente-aprovacao';
      }
      return 'admin-cliente-venda';
    }
    if (ids.partnerId && path.includes('/parceiros/visualizar/')) {
      return 'admin-parceiro';
    }
    if (ids.claimId && path.includes('/sinistro/visualizar/')) {
      return 'admin-sinistro';
    }
    if (ids.claimDocumentId && path.includes('/acervo-de-documentos/')) {
      return 'admin-acervo-documento';
    }
    if (ids.productId && path.includes('/produtos/visualizar/')) {
      return 'admin-produto';
    }
    if (ids.productEditingRequestId && path.includes('/solicitacoes/')) {
      return 'admin-solicitacao-produto';
    }
    if (path.includes('/minha-conta/blog/editar/') || path.includes('/minha-conta/blog/novo')) {
      return 'admin-blog-studio';
    }
    if (path.includes('/minha-conta/inteligencia')) {
      return 'admin-inteligencia';
    }
    if (ids.saleId && path.includes('/meus-seguros/')) {
      return 'usuario-seguro';
    }
    if (ids.claimId && path.includes('/aviso-sinistro/')) {
      return 'usuario-sinistro';
    }
    if (ids.partnerSlug && path.includes('/mobilidade-eletrica/')) {
      return 'site-parceiro-mobilidade';
    }
    if (ids.partnerSlug && path.includes('/paineis-solares/')) {
      return 'site-parceiro-solar';
    }
    if (path.startsWith('/movimento-wosi/preview/') || path.startsWith('/blog/preview/')) {
      return 'site-blog-preview';
    }
    if (
      path === '/movimento-wosi' ||
      path === '/blog' ||
      path.startsWith('/movimento-wosi/categoria/') ||
      path.startsWith('/blog/categoria/')
    ) {
      return 'site-blog-home';
    }
    if (
      ids.blogSlug &&
      (path.startsWith('/movimento-wosi/') || path.startsWith('/blog/'))
    ) {
      return 'site-blog-article';
    }
    if (path.startsWith('/minha-conta')) {
      const idx = segments.indexOf('minha-conta');
      const tail = idx >= 0 ? segments.slice(idx + 1) : segments;
      return tail.length ? `conta-${tail.join('-')}` : 'conta-home';
    }
    return segments.length ? segments.join('-') : 'desconhecida';
  }
}
